import { api, http } from '../client';
import type { PageParams } from '@/types/api';
import type { UUID } from '@/types/domain';
import type {
  Acquisition,
  AcquisitionInput,
  AcquisitionReport,
  AcquisitionStatus,
  Author,
  AuditScanInput,
  Book,
  BookCategory,
  BookCategoryInput,
  BookCopy,
  BookCopyInput,
  BookInput,
  BookSearchParams,
  BorrowingRuleInput,
  CirculationDashboard,
  CirculationReport,
  CopyStatus,
  DamageRecord,
  DamageReportInput,
  DamageStatus,
  Eligibility,
  Fine,
  FineAdjustmentInput,
  FineReport,
  FineSearchParams,
  InventoryAudit,
  InventoryAuditInput,
  InventoryReport,
  InventorySummary,
  IssueInput,
  Library,
  LibraryDashboard,
  LibraryInput,
  LibraryLocation,
  LibraryLocationInput,
  LibraryMember,
  LibraryMemberInput,
  LibraryStatus,
  Loan,
  LoanSearchParams,
  LostBookInput,
  LostBookRecord,
  LostBookStatus,
  ManualFineInput,
  MemberActivityReport,
  MemberStatus,
  OverdueReport,
  PopularBooksReport,
  Publisher,
  ReceiptInput,
  RenewInput,
  Reservation,
  ReservationInput,
  ReservationStatus,
  ReturnInput,
  ReturnResult,
  Supplier,
  SupplierInput,
} from '@/types/library';

/*
 * The library module's endpoints.
 *
 * Two things run through the whole file.
 *
 * The catalogue is never fetched whole. §44 rules out loading it into the
 * browser, so every listing is paged and every search is a server-side query —
 * there is deliberately no `books.all()`.
 *
 * Nothing here calculates. Fines, due dates, renewal eligibility and queue
 * positions all arrive computed, because §38 says a critical rule must not live
 * only in React. Where a screen shows a reason ("cannot be renewed because…"),
 * that string came from the server.
 */

// ------------------------------------------------------------------ library

export const librariesApi = {
  list: () => api.get<Library[]>('/libraries'),
  get: (id: UUID) => api.get<Library>(`/libraries/${id}`),
  create: (payload: LibraryInput) => api.post<Library>('/libraries', payload),
  update: (id: UUID, payload: LibraryInput) => api.put<Library>(`/libraries/${id}`, payload),

  /** Closed, never deleted — every loan ever made names its library. */
  setStatus: (id: UUID, status: LibraryStatus) =>
    api.patch<Library>(`/libraries/${id}/status`, undefined, { params: { status } }),

  /** Overrides the library's defaults for one kind of member. */
  saveRule: (id: UUID, payload: BorrowingRuleInput) =>
    api.put<Library>(`/libraries/${id}/borrowing-rules`, payload),
};

export const libraryLocationsApi = {
  list: (libraryId?: UUID) =>
    api.get<LibraryLocation[]>('/library-locations', { params: { libraryId } }),
  create: (payload: LibraryLocationInput) =>
    api.post<LibraryLocation>('/library-locations', payload),
  update: (id: UUID, payload: LibraryLocationInput) =>
    api.put<LibraryLocation>(`/library-locations/${id}`, payload),
  setStatus: (id: UUID, status: LibraryStatus) =>
    api.patch<LibraryLocation>(`/library-locations/${id}/status`, undefined, { params: { status } }),
};

// ---------------------------------------------------------------- catalogue

export const booksApi = {
  /**
   * The catalogue search.
   *
   * `search` is full text over title, subtitle, subject and tags, and tolerates
   * a misspelling. It is a server-side query on purpose: filtering a school's
   * whole catalogue in the browser is exactly what §44 rules out.
   */
  search: (params: PageParams & BookSearchParams) => api.getPage<Book>('/books', { params }),

  get: (id: UUID) => api.get<Book>(`/books/${id}`),
  create: (payload: BookInput) => api.post<Book>('/books', payload),
  update: (id: UUID, payload: BookInput) => api.put<Book>(`/books/${id}`, payload),

  /** Withdrawn, never deleted: borrowing history points at the title. */
  setStatus: (id: UUID, status: LibraryStatus) =>
    api.patch<Book>(`/books/${id}/status`, undefined, { params: { status } }),

  languages: () => api.get<string[]>('/books/languages'),

  copies: (id: UUID) => api.get<BookCopy[]>(`/books/${id}/copies`),

  uploadCover: (id: UUID, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<Book>(`/books/${id}/cover`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const bookCopiesApi = {
  get: (id: UUID) => api.get<BookCopy>(`/book-copies/${id}`),

  /** Resolves a barcode or an accession number — whatever the scanner read. */
  scan: (code: string) => api.get<BookCopy>('/book-copies/scan', { params: { code } }),

  /** Accessions `quantity` copies at once, numbering them in sequence. */
  create: (payload: BookCopyInput) => api.post<BookCopy[]>('/book-copies', payload),

  update: (id: UUID, payload: BookCopyInput) => api.put<BookCopy>(`/book-copies/${id}`, payload),
  setStatus: (id: UUID, status: CopyStatus, reason?: string) =>
    api.patch<BookCopy>(`/book-copies/${id}/status`, undefined, { params: { status, reason } }),
};

export const catalogueReferenceApi = {
  categories: () => api.get<BookCategory[]>('/book-categories'),
  createCategory: (payload: BookCategoryInput) =>
    api.post<BookCategory>('/book-categories', payload),
  updateCategory: (id: UUID, payload: BookCategoryInput) =>
    api.put<BookCategory>(`/book-categories/${id}`, payload),
  setCategoryStatus: (id: UUID, status: LibraryStatus) =>
    api.patch<BookCategory>(`/book-categories/${id}/status`, undefined, { params: { status } }),

  authors: (params: PageParams & { search?: string }) => api.getPage<Author>('/authors', { params }),
  createAuthor: (payload: { name: string; biography?: string }) =>
    api.post<Author>('/authors', payload),

  publishers: (params: PageParams & { search?: string }) =>
    api.getPage<Publisher>('/publishers', { params }),
  createPublisher: (payload: { name: string; city?: string }) =>
    api.post<Publisher>('/publishers', payload),
};

// ------------------------------------------------------------------ members

export const libraryMembersApi = {
  list: (params: PageParams & { libraryId?: UUID; memberType?: string; status?: MemberStatus }) =>
    api.getPage<LibraryMember>('/library-members', { params }),

  /** The caller's own memberships. Takes no identifier: read from the account. */
  mine: () => api.get<LibraryMember[]>('/library-members/me'),

  get: (id: UUID) => api.get<LibraryMember>(`/library-members/${id}`),

  /** Resolves a membership number or a student's own card. */
  scan: (code: string) => api.get<LibraryMember>('/library-members/scan', { params: { code } }),

  /**
   * Whether they may borrow, and if not, why not — in the server's words.
   * The desk shows the reasons rather than working them out again.
   */
  eligibility: (id: UUID) => api.get<Eligibility>(`/library-members/${id}/eligibility`),

  enrol: (payload: LibraryMemberInput) => api.post<LibraryMember>('/library-members', payload),
  update: (id: UUID, payload: LibraryMemberInput) =>
    api.put<LibraryMember>(`/library-members/${id}`, payload),
  setStatus: (id: UUID, status: MemberStatus, reason?: string) =>
    api.patch<LibraryMember>(`/library-members/${id}/status`, undefined, {
      params: { status, reason },
    }),
};

// -------------------------------------------------------------- circulation

export const circulationApi = {
  loans: (params: PageParams & LoanSearchParams) => api.getPage<Loan>('/circulation/loans', { params }),

  overdue: (libraryId?: UUID) => api.get<Loan[]>('/circulation/loans/overdue', { params: { libraryId } }),

  loan: (id: UUID) => api.get<Loan>(`/circulation/loans/${id}`),

  /** What a member has out right now — not their history. */
  memberLoans: (memberId: UUID) => api.get<Loan[]>(`/circulation/members/${memberId}/loans`),

  /**
   * Lend a copy.
   *
   * Takes scanned codes on both sides so the desk is scan member → scan book →
   * confirm, with no lookup in between.
   */
  issue: (payload: IssueInput) => api.post<Loan>('/circulation/issue', payload),

  /**
   * Take a copy back. The overdue days and the fine are the server's: nothing
   * sent from here influences the amount.
   */
  returnCopy: (payload: ReturnInput) => api.post<ReturnResult>('/circulation/return', payload),

  renew: (id: UUID, payload: RenewInput = {}) =>
    api.post<Loan>(`/circulation/loans/${id}/renew`, payload),
};

// ------------------------------------------------------------- reservations

export const reservationsApi = {
  list: (params: PageParams & { libraryId?: UUID; bookId?: UUID; status?: ReservationStatus[] }) =>
    api.getPage<Reservation>('/reservations', { params }),

  mine: () => api.get<Reservation[]>('/reservations/me'),

  /** The live queue for a title, in order. */
  queue: (bookId: UUID) => api.get<Reservation[]>('/reservations/queue', { params: { bookId } }),

  get: (id: UUID) => api.get<Reservation>(`/reservations/${id}`),

  reserveForSelf: (payload: ReservationInput) =>
    api.post<Reservation>('/reservations/me', payload),
  reserveFor: (payload: ReservationInput) => api.post<Reservation>('/reservations', payload),

  cancel: (id: UUID, reason: string) =>
    api.post<Reservation>(`/reservations/${id}/cancel`, undefined, { params: { reason } }),

  /**
   * Expire uncollected holds and stale queue entries.
   *
   * A button rather than a nightly job, matching every other sweep in this
   * codebase: there is no scheduler, and a job nobody watches is worse than a
   * button somebody presses.
   */
  expireLapsed: () => api.post<number>('/reservations/expire-lapsed'),
};

// -------------------------------------------------------------------- fines

export const libraryFinesApi = {
  list: (params: PageParams & FineSearchParams) => api.getPage<Fine>('/library-fines', { params }),

  mine: () => api.get<Fine[]>('/library-fines/me'),

  forMember: (memberId: UUID) => api.get<Fine[]>(`/library-fines/members/${memberId}`),

  get: (id: UUID) => api.get<Fine>(`/library-fines/${id}`),

  createManual: (payload: ManualFineInput) => api.post<Fine>('/library-fines', payload),

  /**
   * Money taken at the desk — only for a staff fine.
   *
   * A student's fine is a charge in finance and is paid through the fees screen.
   * Taking it here as well would be two systems disagreeing about whether a
   * family has paid.
   */
  recordPayment: (id: UUID, payload: FineAdjustmentInput) =>
    api.post<Fine>(`/library-fines/${id}/payment`, payload),

  /** Never deletes: the waiver is recorded with who, when and why. */
  waive: (id: UUID, payload: FineAdjustmentInput) =>
    api.post<Fine>(`/library-fines/${id}/waive`, payload),

  /** Different from a waiver: this says the debt never existed. */
  cancel: (id: UUID, reason: string) =>
    api.post<Fine>(`/library-fines/${id}/cancel`, undefined, { params: { reason } }),

  lost: (params: PageParams & { status?: LostBookStatus }) =>
    api.getPage<LostBookRecord>('/library-fines/lost', { params }),
  reportLost: (payload: LostBookInput) => api.post<LostBookRecord>('/library-fines/lost', payload),
  recoverLost: (id: UUID, waiveFine: boolean, notes?: string) =>
    api.post<LostBookRecord>(`/library-fines/lost/${id}/recover`, undefined, {
      params: { waiveFine, notes },
    }),
  writeOffLoss: (id: UUID, reason: string) =>
    api.post<LostBookRecord>(`/library-fines/lost/${id}/write-off`, undefined, { params: { reason } }),

  damaged: (params: PageParams & { status?: DamageStatus }) =>
    api.getPage<DamageRecord>('/library-fines/damaged', { params }),
  reportDamage: (payload: DamageReportInput) =>
    api.post<DamageRecord>('/library-fines/damaged', payload),
  setDamageStatus: (id: UUID, status: DamageStatus, notes?: string) =>
    api.patch<DamageRecord>(`/library-fines/damaged/${id}/status`, undefined, {
      params: { status, notes },
    }),
};

// ------------------------------------------------------------- acquisitions

export const acquisitionsApi = {
  suppliers: (activeOnly = false) =>
    api.get<Supplier[]>('/acquisitions/suppliers', { params: { activeOnly } }),
  supplier: (id: UUID) => api.get<Supplier>(`/acquisitions/suppliers/${id}`),
  createSupplier: (payload: SupplierInput) =>
    api.post<Supplier>('/acquisitions/suppliers', payload),
  updateSupplier: (id: UUID, payload: SupplierInput) =>
    api.put<Supplier>(`/acquisitions/suppliers/${id}`, payload),
  setSupplierStatus: (id: UUID, status: LibraryStatus) =>
    api.patch<Supplier>(`/acquisitions/suppliers/${id}/status`, undefined, { params: { status } }),

  list: (params: PageParams & { libraryId?: UUID; supplierId?: UUID; status?: AcquisitionStatus[] }) =>
    api.getPage<Acquisition>('/acquisitions', { params }),

  get: (id: UUID) => api.get<Acquisition>(`/acquisitions/${id}`),

  /** Starts as a draft. No copies exist yet — an order is not a book. */
  create: (payload: AcquisitionInput) => api.post<Acquisition>('/acquisitions', payload),
  update: (id: UUID, payload: AcquisitionInput) =>
    api.put<Acquisition>(`/acquisitions/${id}`, payload),
  markOrdered: (id: UUID) => api.post<Acquisition>(`/acquisitions/${id}/order`),

  /**
   * Record a delivery. This is where stock is created: each received copy is
   * accessioned with its own number. Partial deliveries are normal, and only
   * what arrived becomes a copy.
   */
  receive: (id: UUID, receipts: ReceiptInput[]) =>
    api.post<Acquisition>(`/acquisitions/${id}/receive`, receipts),

  cancel: (id: UUID, reason: string) =>
    api.post<Acquisition>(`/acquisitions/${id}/cancel`, undefined, { params: { reason } }),
};

// ---------------------------------------------------------------- inventory

export const libraryInventoryApi = {
  summary: (libraryId?: UUID) =>
    api.get<InventorySummary>('/library-inventory/summary', { params: { libraryId } }),

  audits: (params: PageParams) => api.getPage<InventoryAudit>('/library-inventory/audits', { params }),
  audit: (id: UUID) => api.get<InventoryAudit>(`/library-inventory/audits/${id}`),

  /** Snapshots what should be on the shelves. Copies out on loan are left out. */
  startAudit: (payload: InventoryAuditInput) =>
    api.post<InventoryAudit>('/library-inventory/audits', payload),

  /** Takes a scanned code, because that is what working down a shelf produces. */
  scan: (id: UUID, payload: AuditScanInput) =>
    api.post<InventoryAudit>(`/library-inventory/audits/${id}/scan`, payload),

  /**
   * Everything unscanned becomes MISSING, misplaced copies are re-shelved where
   * they were found, and copies that had been missing and turned up come back
   * into stock. Nothing is deleted and nobody is charged.
   */
  complete: (id: UUID, notes?: string) =>
    api.post<InventoryAudit>(`/library-inventory/audits/${id}/complete`, undefined, {
      params: { notes },
    }),

  abandon: (id: UUID, reason: string) =>
    api.post<InventoryAudit>(`/library-inventory/audits/${id}/abandon`, undefined, {
      params: { reason },
    }),
};

// ------------------------------------------------- dashboards and reporting

export const libraryDashboardApi = {
  library: (libraryId?: UUID) =>
    api.get<LibraryDashboard>('/library/dashboard', { params: { libraryId } }),
  circulation: () => api.get<CirculationDashboard>('/library/circulation-dashboard'),
};

export const libraryReportsApi = {
  circulation: (from?: string, to?: string) =>
    api.get<CirculationReport>('/library/reports/circulation', { params: { from, to } }),
  circulationCsv: () => download('/library/reports/circulation.csv', {}, 'circulation.csv'),

  overdue: (libraryId?: UUID) =>
    api.get<OverdueReport>('/library/reports/overdue', { params: { libraryId } }),
  overdueCsv: (libraryId?: UUID) =>
    download('/library/reports/overdue.csv', { libraryId }, 'overdue.csv'),

  popularBooks: (from?: string, to?: string, limit = 50) =>
    api.get<PopularBooksReport>('/library/reports/popular-books', { params: { from, to, limit } }),
  popularBooksCsv: (from?: string, to?: string) =>
    download('/library/reports/popular-books.csv', { from, to }, 'popular-books.csv'),

  memberActivity: (from?: string, to?: string, limit = 50) =>
    api.get<MemberActivityReport>('/library/reports/member-activity', {
      params: { from, to, limit },
    }),
  memberActivityCsv: (from?: string, to?: string) =>
    download('/library/reports/member-activity.csv', { from, to }, 'member-activity.csv'),

  inventory: (libraryId?: UUID) =>
    api.get<InventoryReport>('/library/reports/inventory', { params: { libraryId } }),
  inventoryCsv: (libraryId?: UUID) =>
    download('/library/reports/inventory.csv', { libraryId }, 'inventory.csv'),

  fines: (from?: string, to?: string) =>
    api.get<FineReport>('/library/reports/fines', { params: { from, to } }),
  finesCsv: (from?: string, to?: string) =>
    download('/library/reports/fines.csv', { from, to }, 'library-fines.csv'),

  acquisitions: (from?: string, to?: string) =>
    api.get<AcquisitionReport>('/library/reports/acquisitions', { params: { from, to } }),
  acquisitionsCsv: (from?: string, to?: string) =>
    download('/library/reports/acquisitions.csv', { from, to }, 'acquisitions.csv'),
};

/**
 * Fetch a CSV and hand it to the browser.
 *
 * <p>Fetched as a blob rather than opened as a link: the request needs an
 * Authorization header, and a plain anchor cannot carry one.
 */
async function download(path: string, params: Record<string, unknown>, filename: string) {
  const response = await http.get(path, { params, responseType: 'blob' });

  const url = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
